import {OptionsDto} from '@hconnect/tuc-processes/options/types'
import {mapOrderIntakeOptions, OrderIntakeOptionsDto} from '@hconnect/tuc-processes/orderIntake'
import {Contact} from '@hconnect/tuc-processes/types'
import {useMemo} from 'react'
import {useLocation} from 'react-router-dom'

import {usePersistedRmcContact} from '../../../../common/react-query/hooks/queries/usePersistedRmcContact'
import {usePersistedRmcOrder} from '../../../../common/react-query/hooks/queries/usePersistedRmcOrder'
import {useRmcCountryFeatures} from '../../shared/order-editor/hooks/useRmcCountryFeatures'
import {RmcOrderPayload} from '../services/rmcOrderPlacementService'
import {OrderIntakeSite} from '../types/OrderIntakeSite.types'
import {normalizeRmcOptions} from '../utils/normalizeRmcOptions'

import {useRmcOrderIntakeOptionsV2} from './useRmcOrderIntakeOptionsV2'

export interface RmcOrderIntakeSetup {
  // URL Parameters
  jobSiteId: string | null
  isRepeatOrder: boolean

  // Job Site Data
  jobSite: OrderIntakeSite | undefined

  // Options Data
  options: OrderIntakeOptionsDto | undefined
  mappedOptions: OptionsDto
  isOptionsLoading: boolean
  isOptionsError: boolean

  // Contract Data
  /** True if the options contain more than one contract (used for quote selection step) */
  hasMultipleContracts: boolean

  // Persisted Orders (for repeat order flow)
  persistedOrders: RmcOrderPayload[] | undefined

  // Persisted Contact Data
  persistedContact:
    | {
        orderPlacer?: Contact
        siteContact?: Contact
      }
    | undefined
}

/**
 * Hook that consolidates all data fetching and setup logic for RMC order intake.
 * Handles URL params parsing, job site data, options fetching, and normalization.
 *
 * @returns Consolidated setup data for order intake
 */
export const useRmcOrderIntakeSetup = (): RmcOrderIntakeSetup => {
  const location = useLocation<{selectedJobSite?: OrderIntakeSite}>()
  const searchParams = new URLSearchParams(location.search)
  const jobSiteId = searchParams.get('jobSiteId')
  const isRepeatOrder = searchParams.get('repeatOrder') === 'true'

  const countryFeatures = useRmcCountryFeatures()

  // Get job site from navigation state (passed from JobSiteSelectionModal)
  const jobSite = location.state?.selectedJobSite

  // Fetch persisted orders if this is a repeat order flow
  const {data: persistedOrders} = usePersistedRmcOrder(
    isRepeatOrder ? jobSite?.siteNumber || '' : ''
  )

  // Fetch persisted contact data
  const {data: persistedContact} = usePersistedRmcContact(jobSite?.siteNumber || '')

  // Fetch RMC order intake options using the V2 API
  const {
    data: rmcOptions,
    isLoading: isOptionsLoading,
    isError: isOptionsError
  } = useRmcOrderIntakeOptionsV2(
    jobSite?.customerId,
    jobSite?.siteId,
    jobSite?.salesOffice,
    jobSite?.marketArea,
    !!(jobSite?.customerId && jobSite?.siteId)
  )

  // Normalize RMC options (ensure arrays are actual arrays)
  const normalizedOptions = useMemo(() => normalizeRmcOptions(rmcOptions), [rmcOptions])

  // Determine if there are multiple contracts (for quote selection step)
  const hasMultipleContracts = useMemo(() => {
    return (normalizedOptions?.contracts?.length ?? 0) > 1
  }, [normalizedOptions])

  // Map RMC options to OrderIntakeOptionsResponse format
  const mappedOptions = useMemo((): OptionsDto => {
    if (!normalizedOptions) {
      return {
        constraints: {},
        contracts: [],
        workCalendar: {}
      } as unknown as OptionsDto
    }

    return mapOrderIntakeOptions(normalizedOptions, jobSite?.orgUnitId, countryFeatures)
  }, [normalizedOptions, countryFeatures, jobSite?.orgUnitId])

  return {
    jobSiteId,
    isRepeatOrder,
    jobSite,
    options: normalizedOptions,
    mappedOptions,
    isOptionsLoading,
    isOptionsError,
    hasMultipleContracts,
    persistedOrders,
    persistedContact
  }
}
